import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-expenses';

export type Expense = components['schemas']['Expense'];

export type ExpenseState =
  | { status: 'loading' }
  | { status: 'not-found' }
  | { status: 'error' }
  | { status: 'ready'; expense: Expense };

/** `GET /v1/expenses/{id}`, with a reload and a way to take the expense a write returned. */
export function useExpense(expenseId: string) {
  const [state, setState] = useState<ExpenseState>({ status: 'loading' });
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    apiClient
      .GET('/v1/expenses/{expenseId}', { params: { path: { expenseId } } })
      .then(({ data, error, response }) => {
        if (!active) return;
        if (response.status === 404) {
          setState({ status: 'not-found' });
          return;
        }
        if (error !== undefined || !response.ok || data === undefined) {
          setState({ status: 'error' });
          return;
        }
        setState({ status: 'ready', expense: data });
      })
      .catch(() => {
        if (active) setState({ status: 'error' });
      });
    return () => {
      active = false;
    };
  }, [expenseId, reloads]);

  const reload = useCallback(() => setReloads((count) => count + 1), []);
  const replace = useCallback((expense: Expense) => setState({ status: 'ready', expense }), []);

  return { state, reload, replace } as const;
}
